import React, { useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    Button,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { statsStore, supplierStore, authStore } from "../stores";
import { DeviceStat } from "../types";
import Constants from "../constants";
import { resolveStatsSupplierId } from "../utils/statsSupplier";
import { exportToCsv } from "../utils/excelExport";

interface DeviceStatsProps {
    clientId: number | null | undefined;
    startDate?: string;
    endDate?: string;
}

const DeviceStats: React.FC<DeviceStatsProps> = observer(({ clientId, startDate, endDate }) => {
    const { deviceStats, loading, error } = statsStore;
    const isClient = authStore.user && !["okko", "wink", "premiere", "admin"].includes(authStore.user.login);
    const isAdmin = authStore.user?.login === "admin";

    // Загружаем список поставщиков при монтировании
    useEffect(() => {
        if (supplierStore.suppliers.length === 0) {
            supplierStore.fetchSuppliers();
        }
    }, []);

    useEffect(() => {
        console.log("DeviceStats useEffect:", {
            startDate,
            endDate,
            clientId,
        });
        const supplierId = resolveStatsSupplierId(supplierStore.currentSupplier, authStore.user);
        if (startDate && endDate && supplierId) {
            console.log("Fetching device stats for supplier:", supplierId);
            statsStore.fetchDeviceStats(
                clientId,
                new Date(startDate),
                new Date(endDate),
                supplierId,
            );
        }
    }, [clientId, startDate, endDate, supplierStore.currentSupplier?.id, authStore.user?.login, authStore.user?.supplierId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!deviceStats || !deviceStats.data || deviceStats.data.length === 0) {
        return <Alert severity="info">Нет данных по устройствам за выбранный период.</Alert>;
    }

    // Используем данные из API вместо суммирования таблицы
    const totalViews =
        deviceStats.totalViews ??
        deviceStats.data.reduce((sum, device) => sum + parseInt(device.totalViews), 0);
    const totalUniqueVisitors =
        deviceStats.totalUniqueVisitors ??
        deviceStats.data.reduce((sum, device) => sum + parseInt(device.uniqueVisitors), 0);
    const totalPageViews =
        deviceStats.totalPageViews ??
        deviceStats.data.reduce((sum, device) => sum + parseInt(device.pageViews), 0);
    const totalMediaViews =
        deviceStats.totalMediaViews ??
        deviceStats.data.reduce((sum, device) => sum + parseInt(device.mediaViews), 0);

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                    Статистика по устройствам
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                        const csvHeaders = [
                            "Устройство",
                            "Серийный номер",
                            "Клиент",
                            ...(isAdmin ? ["Всего просмотров"] : []),
                            "Зрители",
                            ...(isAdmin ? ["Просмотры страниц"] : []),
                            "Просмотры медиа",
                            "Первая активность",
                            "Последняя активность",
                        ];
                        const csvRows = deviceStats.data.map((item: DeviceStat) => [
                            item.deviceName || "",
                            item.deviceSerial || "",
                            item.clientName || "",
                            ...(isAdmin ? [item.totalViews || "0"] : []),
                            item.uniqueVisitors || "0",
                            ...(isAdmin ? [item.pageViews || "0"] : []),
                            item.mediaViews || "0",
                            item.firstActivity ? new Date(item.firstActivity).toLocaleString("ru-RU") : "",
                            item.lastActivity ? new Date(item.lastActivity).toLocaleString("ru-RU") : "",
                        ]);
                        exportToCsv({
                            filename: `device_stats_${new Date().toISOString().split("T")[0]}`,
                            headers: csvHeaders,
                            data: csvRows,
                            separator: Constants.CSV_SEPARATOR,
                        });
                    }}
                >
                    Экспорт CSV
                </Button>
            </Box>

            {/* Сводная статистика */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Всего устройств
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {deviceStats.data.length}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Уникальных устройств
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {isAdmin && (
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Всего просмотров
                                </Typography>
                                <Typography variant="h4" color="secondary">
                                    {totalViews.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Страницы + Медиа
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {isAdmin && (
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Просмотры страниц
                                </Typography>
                                <Typography variant="h4" color="info.main">
                                    {totalPageViews.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    На устройствах
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Просмотры медиа
                            </Typography>
                            <Typography variant="h4" color="warning.main">
                                {totalMediaViews.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                На устройствах
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Зрители
                            </Typography>
                            <Typography variant="h4" color="success.main">
                                {totalUniqueVisitors.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                На устройствах
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Информационный баннер с пояснением - скрыт для поставщиков */}
            {/* <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Статистика устройств (Abox):</strong> Показывает просмотры
            на всех устройствах поставщика (на рейсах и вне рейсов). Зрители
            могут пересекаться между страницами и медиа.
          </Typography>
        </Alert> */}

            {/* Таблица статистики */}
            <Card>
                <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Устройство</TableCell>
                                    <TableCell>Серийный номер</TableCell>
                                    <TableCell>Клиент</TableCell>
                                    {isAdmin && <TableCell align="right">Всего просмотров</TableCell>}
                                    <TableCell align="right">Зрители</TableCell>
                                    {isAdmin && <TableCell align="right">Просмотры страниц</TableCell>}
                                    <TableCell align="right">Просмотры медиа</TableCell>
                                    <TableCell align="right">Первая активность</TableCell>
                                    <TableCell align="right">Последняя активность</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deviceStats.data.map((device: DeviceStat, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>{device.deviceName}</TableCell>
                                        <TableCell>{device.deviceSerial}</TableCell>
                                        <TableCell>{device.clientName}</TableCell>
                                        {isAdmin && (
                                            <TableCell align="right">
                                                {parseInt(device.totalViews).toLocaleString()}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            {parseInt(device.uniqueVisitors).toLocaleString()}
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell align="right">
                                                {parseInt(device.pageViews).toLocaleString()}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            {parseInt(device.mediaViews).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {new Date(device.firstActivity).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {new Date(device.lastActivity).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
});

export default DeviceStats;
